import { useState } from "react";
import { useKV } from "@/hooks/use-kv-adapter";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { EmployeeDocument } from "@/types/core-hr";
import { FileText, DownloadSimple, Trash, UploadSimple, MagnifyingGlass } from "@phosphor-icons/react";
import { toast } from "sonner";

export function DocumentVault() {
  const [documents, setDocuments] = useKV<EmployeeDocument[]>("core-hr-documents", []);
  const [searchQuery, setSearchQuery] = useState("");
  const [isUploadOpen, setIsUploadOpen] = useState(false);
  const [uploadForm, setUploadForm] = useState<Partial<EmployeeDocument>>({
    type: 'Other',
    title: ''
  });

  const handleUpload = () => {
    if (!uploadForm.title || !uploadForm.type) {
      toast.error("Please fill in all fields");
      return;
    }

    const newDoc: EmployeeDocument = {
      id: crypto.randomUUID(),
      employeeId: 'CURRENT_USER', // In real app, select employee
      title: uploadForm.title,
      type: uploadForm.type as any,
      fileUrl: '#', // Mock URL
      uploadedBy: 'Admin',
      uploadedAt: new Date().toISOString(),
      status: 'Valid'
    };

    setDocuments([newDoc, ...documents]);
    setIsUploadOpen(false);
    setUploadForm({ type: 'Other', title: '' });
    toast.success("Document uploaded successfully");
  };

  const filteredDocs = documents.filter(doc => 
    doc.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
    doc.type.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <div className="relative w-64">
          <MagnifyingGlass className="absolute left-3 top-3 h-4 w-4 text-slate-400" />
          <Input 
            placeholder="Search documents..." 
            className="pl-9"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
        <Button onClick={() => setIsUploadOpen(true)}>
          <UploadSimple className="mr-2 h-4 w-4" /> Upload Document
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>All Documents</CardTitle>
          <CardDescription>Manage employee contracts, IDs, and certificates.</CardDescription>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Title</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Uploaded By</TableHead>
                <TableHead>Date</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredDocs.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-8 text-slate-500">
                    No documents found. Upload one to get started.
                  </TableCell>
                </TableRow>
              ) : (
                filteredDocs.map((doc) => (
                  <TableRow key={doc.id}>
                    <TableCell className="font-medium flex items-center gap-2">
                      <FileText className="text-blue-500" /> {doc.title}
                    </TableCell>
                    <TableCell>{doc.type}</TableCell>
                    <TableCell>{doc.uploadedBy}</TableCell>
                    <TableCell>{new Date(doc.uploadedAt).toLocaleDateString()}</TableCell>
                    <TableCell>
                      <Badge variant="outline" className={doc.status === 'Valid' ? 'text-green-600 border-green-200 bg-green-50' : 'text-amber-600 border-amber-200 bg-amber-50'}>
                        {doc.status}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <Button variant="ghost" size="sm">
                        <DownloadSimple className="h-4 w-4" />
                      </Button>
                      <Button variant="ghost" size="sm" className="text-red-500 hover:text-red-700">
                        <Trash className="h-4 w-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Dialog open={isUploadOpen} onOpenChange={setIsUploadOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Upload Document</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Document Title</Label>
              <Input 
                value={uploadForm.title} 
                onChange={(e) => setUploadForm({...uploadForm, title: e.target.value})}
                placeholder="e.g. Employment Contract" 
              />
            </div>
            <div className="space-y-2">
              <Label>Document Type</Label>
              <Select 
                value={uploadForm.type} 
                onValueChange={(v) => setUploadForm({...uploadForm, type: v as any})}
              >
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="ID Proof">ID Proof</SelectItem>
                  <SelectItem value="Contract">Contract</SelectItem>
                  <SelectItem value="Tax">Tax Document</SelectItem>
                  <SelectItem value="Certificate">Certificate</SelectItem>
                  <SelectItem value="Resume">Resume</SelectItem>
                  <SelectItem value="Other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>File</Label>
              <Input type="file" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsUploadOpen(false)}>Cancel</Button>
            <Button onClick={handleUpload}>Upload</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
